import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";

export interface StylePresetRecord {
  id: string;
  name: string;
  category: string;
  styleData: Record<string, unknown>;
  source: "tenant" | "global";
  createdAt: string;
  updatedAt: string;
}

export async function fetchStylePresets(): Promise<StylePresetRecord[]> {
  const response = await apiFetch<{ data: { items: StylePresetRecord[] } }>("/crm/pages/style-presets?limit=100");
  return response.data.items ?? [];
}

export async function createStylePreset(input: {
  name: string;
  category?: string;
  styleData: Record<string, unknown>;
}): Promise<StylePresetRecord> {
  const response = await apiFetch<{ data: StylePresetRecord }>("/crm/pages/style-presets", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function deleteStylePreset(id: string): Promise<void> {
  await apiFetch(`/crm/pages/style-presets/${id}`, { method: "DELETE" });
}

export function useStylePresets() {
  const [presets, setPresets] = useState<StylePresetRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setPresets(await fetchStylePresets());
    } catch (error) {
      setPresets([]);
      setError(error instanceof ApiError ? error.message : "Failed to load style presets");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const savePreset = useCallback(async (input: {
    name: string;
    category?: string;
    styleData: Record<string, unknown>;
  }) => {
    setIsSaving(true);
    setError(null);

    try {
      const preset = await createStylePreset(input);
      setPresets((current) => [...current.filter((item) => item.source === "global"), preset, ...current.filter((item) => item.source === "tenant")]);
      return preset;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to save style preset";
      setError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const removePreset = useCallback(async (id: string) => {
    setError(null);

    try {
      await deleteStylePreset(id);
      setPresets((current) => current.filter((preset) => preset.id !== id));
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to delete style preset";
      setError(message);
      throw error;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    presets,
    isLoading,
    isSaving,
    error,
    refresh,
    savePreset,
    removePreset,
  };
}
